import React, { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "civichearth_install_dismissed";

export const InstallPrompt = () => {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIos) {
      const t = setTimeout(() => { setShowIos(true); setVisible(true); }, 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferred(null);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!visible) return null;

  return (
    /* Mobile floating-element sweep (2026-02): signed-in <lg has the bottom
       tab bar (4rem + safe area) AND the feedback pill shelf above it —
       clear both (4rem tab bar + 4rem shelf). Logged-out only clears the
       pill's bottom shelf. Old fixed bottom-20 overlapped the tab bar on
       notched iPhones (safe-area inset) — exactly where the iOS tip shows. */
    <div
      data-testid="install-prompt"
      className={`lg:hidden fixed inset-x-3 z-50 animate-fade-up ${
        user
          ? "bottom-[calc(4rem+env(safe-area-inset-bottom)+4rem)]"
          : "bottom-[calc(env(safe-area-inset-bottom)+4rem)]"
      }`}
    >
      <div className="civic-glass border border-forest/15 rounded-2xl shadow-lg shadow-forest/10 p-4 flex items-start gap-3">
        <img src="/icon-192.png" alt="CivicHearth" className="w-11 h-11 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-forest leading-tight">Add CivicHearth to your home screen</div>
          {showIos ? (
            <div className="text-xs text-neutral-600 mt-1 flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3.5 h-3.5 inline text-forest" /> Share, then
              <PlusSquare className="w-3.5 h-3.5 inline text-forest" /> "Add to Home Screen"
            </div>
          ) : (
            <button
              data-testid="install-prompt-btn"
              onClick={install}
              className="mt-2 text-xs font-medium px-4 py-2 rounded-full bg-forest text-white hover:bg-forest/90 inline-flex items-center gap-1.5 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Install app
            </button>
          )}
        </div>
        <button data-testid="install-prompt-dismiss" onClick={dismiss} aria-label="Dismiss" className="text-neutral-400 hover:text-neutral-600 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
